import { Alert, SxProps, Theme } from "@mui/material";
import {
  SignatureStatus,
  SignatureType,
  SubmissionDocumentType,
  SubmissionStatus,
} from "generated-graphql/graphql";

interface SubmissionDocumentLike {
  type?: SubmissionDocumentType | null;
  externalDocumentId?: string | null;
}

interface ReviewerLike {
  isSigner?: boolean | null;
  signatureStatus?: SignatureStatus | null;
}

interface SubmissionLike {
  status?: SubmissionStatus | null;
  signatureType?: SignatureType | null;
  documents?: SubmissionDocumentLike[] | null;
  reviewers?: ReviewerLike[] | null;
}

interface Props {
  submission: SubmissionLike;
  sx?: SxProps<Theme>;
}

// Surfaces the same blocked-advance state that the API enforces in
// `performSetToInReview`. When this banner is visible the user already knows
// from the submission view that completing review will not advance the
// submission until the signature flow is fixed.
export function SignaturePreparationBanner({ submission, sx }: Props) {
  if (submission.signatureType !== SignatureType.Digital) {
    return null;
  }
  if (
    submission.status !== SubmissionStatus.EncampInitialReview &&
    submission.status !== SubmissionStatus.InReview
  ) {
    return null;
  }

  const submissionTypeDocs = (submission.documents ?? []).filter(
    (d) => d.type === SubmissionDocumentType.Submission
  );
  const missingExternalDoc = submissionTypeDocs.some((d) => d.externalDocumentId == null);
  const pendingSigner = (submission.reviewers ?? []).some(
    (r) => r.isSigner && r.signatureStatus !== SignatureStatus.Sent
  );

  let message: string | null = null;
  if (missingExternalDoc) {
    message =
      "Action needed: the digital signature document must be re-prepared before this submission can advance to review.";
  } else if (pendingSigner) {
    message =
      "Action needed: the signer's document has not been sent yet. Prepare and send the signature request to allow this submission to advance to review.";
  }

  if (!message) {
    return null;
  }

  return (
    <Alert severity="warning" sx={sx}>
      {message}
    </Alert>
  );
}
